import { useState, useEffect, useCallback, useRef } from 'react';
import { useApp, useAppState } from '../../../context/AppContext';
import QuizzAPI from '../../../api/quizz';
import { useErrorHandler } from '../../../shared/hooks/useErrorHandler';
import NavigationService from '../../../utils/history';
import { IconButton } from '../../../insfrastructure/ui/button';

/**
 * Hook personalizado para manejar quizzes
 * Reemplaza QuizzController
 * 
 * @returns {Object} Objeto con datos y funciones para manejar quizzes
 */
export const useQuizz = () => {
    const { dispatch, notify, removeNotification } = useApp();
    const quizzes = useAppState('quizzes');
    const vquizz = useAppState('vquizz');
    const handleError = useErrorHandler();
    
    const [data, setData] = useState(quizzes || []);
    const [view, setView] = useState(vquizz || null);
    const [quizz, setQuizz] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [removeModal, setRemoveModal] = useState(false);
    
    // Ref para evitar ejecuciones múltiples
    const isExecutingRef = useRef(false);

    // Columnas para la tabla
    const columns = [
        { key: 'title', label: 'Nombre', style: { width: "100%" } },
        { key: 'segment', label: 'Programa', style: { width: "100%" } },
        { key: 'actions', label: 'Acciones' }
    ];

    // Custom elements para la tabla
    const customElements = {
        actions: (data) => (
            <div style={{ width: 200 }}>
                <IconButton onClick={() => copyUrl(data)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 6H14C16 6 16 5 16 4C16 2 15 2 14 2H10C9 2 8 2 8 4C8 6 9 6 10 6Z" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16 4.02002C19.33 4.20002 21 5.43002 21 10V16C21 20 20 22 15 22H9C4 22 3 20 3 16V10C3 5.44002 4.67 4.20002 8 4.02002" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => goView(data._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42004 13.98 8.42004 12C8.42004 10.02 10.02 8.42004 12 8.42004C13.98 8.42004 15.58 10.02 15.58 12Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.39997C18.82 5.79997 15.53 3.71997 12 3.71997C8.46997 3.71997 5.17997 5.79997 2.88997 9.39997C1.98997 10.81 1.98997 13.18 2.88997 14.59C5.17997 18.19 8.46997 20.27 12 20.27Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => goReport(data)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 11V17L11 15" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9 17L7 15" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M22 10H18C15 10 14 9 14 6V2L22 10Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => openRemove(data._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 5.97998C17.67 5.64998 14.32 5.47998 10.98 5.47998C9 5.47998 7.02 5.57998 5.04 5.77998L3 5.97998" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M18.85 9.14001L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79002C6.00002 22 5.91002 20.78 5.80002 19.21L5.15002 9.14001" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10.33 16.5H13.66" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.5 12.5H14.5" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            </div>
        ),
        tags: data => data.tags?.map((e, i) => <span key={i} className="bootcamp-tag">{e}</span>),
        segment: data => (
            <span>
                {data.segment
                    ?.toLowerCase()
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
            </span>
        )
    };

    // Sincronizar con estado global
    useEffect(() => {
        if (quizzes) setData(quizzes);
    }, [quizzes]);

    useEffect(() => {
        if (vquizz) {
            setView(vquizz);
            setQuizz(vquizz);
        }
    }, [vquizz]);

    /**
     * Obtener todos los quizzes
     */
    const getAll = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await QuizzAPI.getAll();
            const quizzesList = Array.isArray(res) ? res : [];
            
            dispatch({ type: 'SET_QUIZZES', payload: quizzesList });
            setData(quizzesList);
            return quizzesList;
        } catch (error) {
            handleError(error, 'Error al obtener los quizzes');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, handleError]);

    /**
     * Obtener quiz por ID
     */
    const getById = useCallback(async (id) => {
        if (!id) return null;

        setIsLoading(true);
        try {
            // Intentar obtener del estado local primero (optimización)
            const currentQuizzes = quizzes || [];
            const foundQuizz = currentQuizzes.find(e => e._id === id);
            
            if (foundQuizz) {
                dispatch({ type: 'SET_VQUIZZ', payload: foundQuizz });
                setView(foundQuizz);
                setCurrentId(id);
            }

            // Siempre obtener del servidor para asegurar datos actualizados
            const res = await QuizzAPI.getById(id);
            
            dispatch({ type: 'SET_VQUIZZ', payload: res });
            setView(res);
            setCurrentId(id);
            return res;
        } catch (error) {
            handleError(error, 'Error al obtener el quiz');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [quizzes, dispatch, handleError]);

    /**
     * Crear nuevo quiz
     */
    const create = useCallback(async (data) => {
        if (isExecutingRef.current) return;
        isExecutingRef.current = true;

        try {
            const res = await QuizzAPI.create(data);
            await getAll(); // Refrescar lista
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Quiz creado correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al crear el quiz');
            throw error;
        } finally {
            isExecutingRef.current = false;
        }
    }, [getAll, notify, handleError]);

    /**
     * Actualizar quiz
     */
    const update = useCallback(async (id, data) => {
        setIsLoading(true);
        try {
            const res = await QuizzAPI.update(id, data);
            await getAll(); // Refrescar lista
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Quiz actualizado correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al actualizar el quiz');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError]);

    /**
     * Eliminar quiz
     */
    const remove = useCallback(async () => {
        if (!currentId) {
            notify({ 
                title: "Error", 
                type: "error", 
                text: "No se ha seleccionado un quiz para eliminar" 
            });
            return;
        }

        setIsLoading(true);
        try {
            const res = await QuizzAPI.remove(currentId);
            await getAll(); // Refrescar lista
            setRemoveModal(false);
            setCurrentId(null);
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Quiz eliminado correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al eliminar el quiz');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [currentId, getAll, notify, handleError]);

    /**
     * Descargar reporte del quiz
     */
    const downloadReport = useCallback(async (id, fileName = "report") => {
        try {
            const notifyId = notify({ 
                title: "Reportería", 
                type: "info", 
                text: "Creando reporte", 
                time: 60000 
            });
            
            const res = await QuizzAPI.getReport(id);
            const disp = res.headers['content-disposition'];
            const filename = `${fileName}.csv`;
            const type = res.headers['content-type'] || 'text/csv;charset=utf-8';
            const blob = type.includes('text/csv')
                ? new Blob(['\uFEFF', res.data], { type })
                : new Blob([res.data], { type });

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            
            removeNotification({ id: notifyId });
            notify({ 
                title: "Reportería", 
                type: "success", 
                text: "Descargando" 
            });
        } catch (error) {
            handleError(error, 'Error al descargar el reporte');
            throw error;
        }
    }, [notify, removeNotification, handleError]);

    /**
     * Copiar URL del quiz al portapapeles
     */
    const copyUrl = useCallback(async (data) => {
        const url = `https://quiz.academia.ñam.org/quiz/${data.slug}`;
        try {
            await navigator.clipboard.writeText(url);
            notify({ 
                title: "Enlace copiado!", 
                type: "info" 
            });
        } catch (err) {
            notify({ 
                title: "No se pudo copiar la URL", 
                type: "error" 
            });
        }
    }, [notify]);

    /**
     * Navegar a vista del quiz
     */
    const goView = useCallback((id) => {
        setCurrentId(id);
        NavigationService.navigateTo(`/quizz/${id}`);
    }, []);

    /**
     * Generar y descargar reporte
     */
    const goReport = useCallback(async (data) => {
        const id = data._id;
        const fileName = data.slug || data.title?.replace(/ /g, "_") || "report";
        await downloadReport(id, fileName);
    }, [downloadReport]);

    /**
     * Abrir modal de eliminación
     */
    const openRemove = useCallback((id) => {
        setRemoveModal(true);
        setCurrentId(id);
    }, []);

    /**
     * Navegar a crear nuevo quiz
     */
    const newQuizz = useCallback(() => {
        NavigationService.navigateTo("/quizz/create");
    }, []);

    /**
     * Navegar atrás
     */
    const goBack = useCallback(() => {
        NavigationService.navigateBack();
    }, []);

    return {
        // Datos
        data,
        quizzes,
        view,
        vquizz,
        quizz,
        columns,
        customElements,
        
        // Estado
        isLoading,
        currentId,
        removeModal,
        setRemoveModal,
        
        // Funciones CRUD
        getAll,
        getById,
        create,
        update,
        remove,
        
        // Funciones especiales
        copyUrl,
        goView,
        goReport,
        downloadReport,
        openRemove,
        newQuizz,
        goBack,
        
        // Helpers
        refresh: getAll,
    };
};

export default useQuizz;

